import React, { createContext, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  Currency,
  setActiveCurrency,
  getCurrencyConfig,
  formatCurrency as formatCurrencyValue,
} from '../utils/formatters';

interface CurrencyContextType {
  currency: Currency;
  symbol: string;
  decimalPlaces: number;
  formatCurrency: (amount: number | string | null | undefined) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const currency: Currency = user?.company?.currency === 'LEK' ? 'LEK' : 'EUR';

  // Keeps the module-level formatCurrency()/CURRENCY_SYMBOL (used by many
  // pages that don't consume this context directly) in sync with the
  // logged-in user's company currency.
  useEffect(() => {
    setActiveCurrency(currency);
  }, [currency]);

  const value = useMemo<CurrencyContextType>(() => {
    const config = getCurrencyConfig(currency);
    return {
      currency,
      symbol: config.symbol,
      decimalPlaces: config.decimalPlaces,
      formatCurrency: formatCurrencyValue,
    };
  }, [currency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};
